// Which widgets each dashboard view ships with, where they sit on the
// desktop grid, and which localStorage key keeps the viewer's layout.
// Pure (no React, no CSS module) so the per-persona defaults and the
// storage keys are unit tested: dashboardLayout.test.ts, run with
// `tsx --test src/pages/dashboard/dashboardLayout.test.ts` from apps/admin/.

import type { CSSProperties } from 'react';
import { resolvePersonaId, type PublicUser } from '@evi/shared';
import { SALES_DASHBOARD_DEFAULTS } from '../../lib/salesDashboard';

export type WidgetId =
  | 'kpi_revenue' | 'kpi_revenue_all_time' | 'kpi_sessions' | 'kpi_chargers' | 'kpi_energy'
  | 'chart_revenue' | 'chart_sessions' | 'chart_network'
  | 'recent_sessions' | 'sessions' | 'alerts'
  | 'pending_payouts' | 'top_revenue_locations' | 'charger_uptime'
  | 'session_trends' | 'per_location_revenue' | 'charger_utilization'
  | 'settlement_summary' | 'start_timeouts'
  | 'book_locations' | 'book_partners' | 'book_sessions' | 'book_energy';
// Note: 'map' was removed from this union as part of the full-bleed
// redesign. The destination map is now the dashboard background (see
// ./DashboardMapBackground.tsx) rather than a widget inside the grid.
// loadLayout() filters any unknown IDs out of stored layouts, so
// existing localStorage entries with 'map' silently drop the orphan
// on next load, no migration step required.

/**
 * Which dashboard the viewer gets. The Super Admin (the EVI team) has
 * its own default; every other operator persona keeps DEFAULT_LAYOUT,
 * the site personas keep the site view of it and Sales staff keep the
 * book.
 */
export type DashboardView = 'super_admin' | 'operator' | 'site' | 'sales_book';

/** The Super Admin persona, the same test as the Super Admin menu and its
 *  Today landing (lib/todayInbox.ts landsOnToday): role super_admin with no
 *  template and not a Sales Partner. */
export function isSuperAdminPersona(
  user: Partial<Pick<PublicUser, 'role' | 'template' | 'is_sales_agent'>> | null | undefined,
): boolean {
  return !!user && resolvePersonaId({
    role: user.role ?? null, template: user.template ?? null, is_sales_agent: user.is_sales_agent ?? false,
  }) === 'super_admin';
}

/** Sales book first (DashboardPage salesBook), then the site view
 *  (useSiteDashboard), then the Super Admin persona. */
export function dashboardView(v: { salesBook: boolean; isSite: boolean; superAdmin: boolean }): DashboardView {
  if (v.salesBook) return 'sales_book';
  if (v.isSite) return 'site';
  return v.superAdmin ? 'super_admin' : 'operator';
}

export const DEFAULT_LAYOUT: WidgetId[] = [
  // Sparse default: seven widgets only. The dashboard's headline
  // visual is the full-bleed satellite map; the default ships with
  // enough widgets to convey "this is a map; here are key stats;
  // here's the revenue rhythm; here's what's happening; tap to drill
  // in" but no more. The remaining widgets in WIDGET_CATALOG are
  // available via the "Add widget" drawer in edit mode.
  // KPI order drives both the mobile/tablet 2x2 flow (left-to-right,
  // top-to-bottom when inline placement is gated off) and the desktop
  // grid via DEFAULT_PLACEMENTS below. Revenue + Energy on top, Chargers
  // + Active Sessions on the bottom: clusters the financial pair above
  // the operational pair.
  'kpi_revenue', 'kpi_energy', 'kpi_chargers', 'kpi_sessions',
  'chart_revenue',
  'recent_sessions', 'alerts', 'start_timeouts',
];

/**
 * Explicit grid placement for the seven default-shipped widgets in a
 * 12-column grid (60px auto-rows). L-shape: left column filled top
 * to bottom (KPIs + revenue chart), bottom row filled left to right
 * (sessions + anomalies). The L's interior (cols 5-12, rows 1-7) is
 * an 8x7 pocket of empty cells that the satellite map shows through.
 *
 *   ┌─────┬─────┬─────────────────────────────────────────┐
 *   │ KPI │ KPI │                                         │
 *   │  R  │  E  │                                         │
 *   ├─────┼─────┤                                         │
 *   │ KPI │ KPI │                                         │
 *   │  C  │  S  │            (map breathes through        │
 *   ├─────┴─────┤             8 cols x 10 rows of the     │
 *   │           │             L's interior)               │
 *   │           │                                         │
 *   │  Revenue  │                                         │
 *   │   chart   │                                         │
 *   │           │                                         │
 *   │           │                                         │
 *   ├───────────┴─────────────┬───────────────────────────┤
 *   │                         │                           │
 *   │     Recent Sessions     │        Anomalies          │
 *   │     (cols 1-6)          │        (cols 7-12)        │
 *   │                         │                           │
 *   └─────────────────────────┴───────────────────────────┘
 *
 * The chart sits flush under the KPI block (cols 1-4) rather than
 * the previously-centered cols 4-9 placement, so the entire left
 * column above the bottom row reads as one widget stack. That keeps
 * the upper-right and middle-right of the canvas open for map
 * interaction (clicking markers, scrubbing for clusters) without
 * any widget chrome competing for that space.
 *
 * Keyed by widget ID, not array index. Operator-added widgets
 * (which have no entry here) auto-flow with their .size_sm/md/lg
 * class via grid-auto-flow: dense (defined in the CSS module) and
 * pack into the L's interior pocket so they don't push the canvas
 * taller.
 *
 * Trade-off: drag-reorder on these seven widgets is visually a
 * no-op because placement is ID-keyed; the user's array reorder
 * persists in localStorage but the rendered position stays
 * anchored. Lifting placement into the stored layout (so drag-to-
 * customize affects placement) is filed for a follow-up.
 */
export const DEFAULT_PLACEMENTS: Partial<Record<WidgetId, CSSProperties>> = {
  kpi_revenue:     { gridColumn: '1 / 3',   gridRow: '1 / 3'  },
  kpi_energy:      { gridColumn: '3 / 5',   gridRow: '1 / 3'  },
  kpi_chargers:    { gridColumn: '1 / 3',   gridRow: '3 / 5'  },
  kpi_sessions:    { gridColumn: '3 / 5',   gridRow: '3 / 5'  },
  // Revenue chart sits flush under the KPI cluster: 4 cols wide
  // (matching the cluster) x 5 rows tall. 5 rows = 300px + 48px
  // internal grid gaps = 348px cell. Content stack inside is
  // ~290-310px (header pills + TrendChart/empty state + footer
  // pills, with the empty state placeholder set to flex:1 so it
  // adapts to whatever vertical room is left in the cell). The
  // previous 6-row sizing left ~70px of empty .widget glass below
  // the content; 5 rows brings the bottom edge of the chrome up
  // to hug the content.
  //
  // Together with the KPIs above, the chart forms the L's
  // vertical leg in cols 1-4.
  chart_revenue:   { gridColumn: '1 / 5',   gridRow: '5 / 10'  },
  // Bottom row forms the L's horizontal leg: sessions on the
  // left half, anomalies on the right, adjacent at the
  // centerline. Pinned to rows 11-14: row 10 is the 1fr flex
  // spacer defined on .grid in the module CSS, which grows to
  // push this row against the bottom of the dashboard area on
  // taller viewports while collapsing to 0 on shorter ones. The
  // map breathes through the spacer band between the chart's
  // bottom edge (row 9) and this row's top edge (row 11).
  // Bottom row is three across since Start timeouts (TMW 4.5) joined
  // the default set: what happened / what is broken / what is being
  // started but never plugged in.
  recent_sessions: { gridColumn: '1 / 5',   gridRow: '11 / 15' },
  alerts:          { gridColumn: '5 / 9',   gridRow: '11 / 15' },
  start_timeouts:  { gridColumn: '9 / 13',  gridRow: '11 / 15' },
};

/**
 * The EVI team's dashboard (Warren, 28 Oct 2026): "Revenue Today / All
 * time Revenue, Energy (MTD) / Active Sessions" across the top, Chargers
 * online out of the default, and Start timeouts replaced by a Sessions
 * widget that switches between All sessions and Active sessions. Recent
 * sessions and Anomalies keep their cells. Chargers online and Start
 * timeouts stay in the add-widget catalog. The order is also the
 * phone and tablet flow, so the KPI 2x2 reads the same there.
 */
export const SUPER_ADMIN_DEFAULT_LAYOUT: WidgetId[] = [
  'kpi_revenue', 'kpi_revenue_all_time', 'kpi_energy', 'kpi_sessions',
  'chart_revenue',
  'recent_sessions', 'alerts', 'sessions',
];

/** DEFAULT_PLACEMENTS' L-shape with the Super Admin's tiles: the KPI 2x2
 *  is the financial pair over the operational pair, and the Sessions
 *  widget takes Start timeouts' cell (cols 9-12, rows 11-14). A widget
 *  with no entry here (Chargers online or Start timeouts added back from
 *  the catalog) auto-flows into the map pocket instead of landing on top
 *  of a placed tile. */
export const SUPER_ADMIN_PLACEMENTS: Partial<Record<WidgetId, CSSProperties>> = {
  kpi_revenue:          { gridColumn: '1 / 3',   gridRow: '1 / 3'  },
  kpi_revenue_all_time: { gridColumn: '3 / 5',   gridRow: '1 / 3'  },
  kpi_energy:           { gridColumn: '1 / 3',   gridRow: '3 / 5'  },
  kpi_sessions:         { gridColumn: '3 / 5',   gridRow: '3 / 5'  },
  chart_revenue:        { gridColumn: '1 / 5',   gridRow: '5 / 10'  },
  recent_sessions:      { gridColumn: '1 / 5',   gridRow: '11 / 15' },
  alerts:               { gridColumn: '5 / 9',   gridRow: '11 / 15' },
  sessions:             { gridColumn: '9 / 13',  gridRow: '11 / 15' },
};

export const SALES_PLACEMENTS: Partial<Record<WidgetId, CSSProperties>> = {
  book_locations: { gridColumn: '1 / 3', gridRow: '1 / 3' },
  book_partners: { gridColumn: '3 / 5', gridRow: '1 / 3' },
  book_sessions: { gridColumn: '1 / 3', gridRow: '3 / 5' },
  book_energy: { gridColumn: '3 / 5', gridRow: '3 / 5' },
  chart_network: { gridColumn: '1 / 5', gridRow: '5 / 11' },
  alerts: { gridColumn: '1 / 7', gridRow: '12 / 16' },
  start_timeouts: { gridColumn: '7 / 13', gridRow: '12 / 16' },
};

// Storage key bumped v2 -> v3 to retire saved layouts that captured
// the previous KPI ordering (Revenue/Sessions/Chargers/Energy). The
// new DEFAULT_LAYOUT clusters the financial pair on top (Revenue +
// Energy) and the operational pair on the bottom (Chargers + Active
// Sessions); without the bump, any operator who'd opened the
// dashboard once on v2 would keep seeing the old order because
// loadLayout() reads the stored array first. main.tsx's defensive
// cleanup block reaps the dead v1/v2 keys on next boot.
// v4: Start timeouts joined DEFAULT_LAYOUT; a stored v3 layout would
// never show it (the add-widget drawer only opens from the empty state).
export const STORAGE_KEY = 'evi.admin.dashboard.layout.v4';

// v5, Super Admin only (28 Oct 2026): SUPER_ADMIN_DEFAULT_LAYOUT. The page
// saves the layout it loads, so every Super Admin who has opened the
// dashboard holds the old default under v4 and would never see the new
// tiles. Its own key, so the operator, site and sales book layouts saved
// under v4 stay as they are (and v4 is not reaped in main.tsx: the other
// personas still read it). The suffix keeps a future v5 for everyone else
// from colliding with this one.
export const SUPER_ADMIN_STORAGE_KEY = 'evi.admin.dashboard.layout.v5.super-admin';

export function dashboardDefaultLayout(view: DashboardView): WidgetId[] {
  if (view === 'sales_book') return [...SALES_DASHBOARD_DEFAULTS];
  return view === 'super_admin' ? SUPER_ADMIN_DEFAULT_LAYOUT : DEFAULT_LAYOUT;
}

export function dashboardPlacements(view: DashboardView): Partial<Record<WidgetId, CSSProperties>> {
  if (view === 'sales_book') return SALES_PLACEMENTS;
  return view === 'super_admin' ? SUPER_ADMIN_PLACEMENTS : DEFAULT_PLACEMENTS;
}

export function dashboardLayoutKey(view: DashboardView): string {
  if (view === 'sales_book') return `${STORAGE_KEY}.sales-book`;
  if (view === 'site') return `${STORAGE_KEY}.site`;
  return view === 'super_admin' ? SUPER_ADMIN_STORAGE_KEY : STORAGE_KEY;
}
